import DataTableCellField from "@/components/data-table/components/DataTableCellField";
import { useDataTableQuery } from "@/components/data-table/hooks/useDataTableQuery";
import { QueryTable } from "@/components/query-table/QueryTable";
import { MenubarItem, MenubarShortcut } from "@/components/ui/menubar";
import MonthYearNavigation from "@/components/MonthYearNavigation";
import {
  readProjectLimitWithRole,
  readProjectLimitWithDepartment,
} from "@/schemas/projectLimit";
import { SuccessFetchListResponse } from "@/services/api";
import { ReadProject } from "@/types/project";
import { ReadProjectLimit } from "@/types/projectLimit";
import { UseQueryResult } from "@tanstack/react-query";
import { memo, useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useProjectBreadcrumb } from "@/modules/projects/hooks/useProjectBreadcrumb";
import ProjectLimitUpsertDialog from "./ProjectLimitUpsertDialog";
import ProjectLimitForm from "./ProjectLimitForm";
import { useProjectLimitsContext } from "./ProjectLimitsContext";
import { UseDataTableOptions } from "@/types/dataTableSettings";
import ModKeyChar from "@/components/ModKeyChar";
import useProjectLimitsShortcuts from "@/modules/projects/hooks/useProjectLimitsShortcuts";
import { ColumnDef, Row } from "@tanstack/react-table";
import Title from "@/components/Title";
import { ProjectDetailsAndNavigation } from "../ProjectDetailsAndNavigation";
import { isProjectRoleDependent } from "@/modules/projects/lib/isProjectRoleDependent";
import { useModulePermissions } from "@/modules/account/hooks/useModulePermissions";
import { useCheckIsProjectLocked } from "@/modules/projects/hooks/useCheckIsProjectLocked";
import { Module, DEFAULT_DATE_FORMAT } from "@/constants";
import { formatDate } from "@/lib/utils/formatters";
import { ProjectLimitUpsertButton } from "./ProjectLimitUpsertButton";
import { MenubarContainer } from "@/components/MenubarContainer";
import { DirtyAwareCell } from "@/contexts/FormTableCellDirtyContext";

interface ProjectLimitEditorProps<P extends ReadProjectLimit> {
  name: string;
  projectQuery: UseQueryResult<ReadProject>;
  schema:
    | typeof readProjectLimitWithDepartment
    | typeof readProjectLimitWithRole;
  initialColumnVisibility: Record<string, boolean>;
  selectedMonthYear: Date;
  onMonthChange: (date: Date) => void;
  options: UseDataTableOptions<P>;
  projectId: string;
}

const ProjectLimitEditor = <P extends ReadProjectLimit>({
  projectQuery,
  schema,
  initialColumnVisibility,
  name,
  selectedMonthYear,
  onMonthChange,
  options,
  projectId,
}: ProjectLimitEditorProps<P>) => {
  const { t } = useTranslation();
  const { can_update } = useModulePermissions();
  const isProjectLocked = useCheckIsProjectLocked();
  useProjectLimitsShortcuts();

  const { project, getItemId, setIsAddLimitsDialogOpen, baseline } =
    useProjectLimitsContext();

  const breadcrumbs = useProjectBreadcrumb(projectQuery, [
    {
      label: t("projects.breadcrumb.limits"),
    },
  ]);

  const onAddLimits = useCallback(() => {
    setIsAddLimitsDialogOpen(true);
  }, [setIsAddLimitsDialogOpen]);

  const { query } = useDataTableQuery<SuccessFetchListResponse<P>>();
  const columnOverrides: Record<string, ColumnDef<P, number>> = useMemo(
    () => ({
      hours: {
        id: "hours",
        cell: ({ row }) => {
          const fieldPath = `stores.${row.original.store_id}.${getItemId(row.original)}.hours`;
          return (
            <DirtyAwareCell fieldPath={fieldPath}>
              <DataTableCellField name={fieldPath} type="number" />
            </DirtyAwareCell>
          );
        },
      },
      rate: {
        id: "rate",
        cell: ({ row }) => {
          const fieldPath = `stores.${row.original.store_id}.${getItemId(row.original)}.rate`;
          return (
            <DirtyAwareCell fieldPath={fieldPath}>
              <DataTableCellField name={fieldPath} type="number" />
            </DirtyAwareCell>
          );
        },
      },
      addition: {
        id: "addition",
        cell: ({ row }) => {
          const fieldPath = `stores.${row.original.store_id}.${getItemId(row.original)}.addition`;
          return (
            <DirtyAwareCell fieldPath={fieldPath}>
              <DataTableCellField name={fieldPath} type="number" />
            </DirtyAwareCell>
          );
        },
      },
      status: {
        id: "status",
        cell: ({ row }: { row: Row<P> }) =>
          t(`storeStatuses.${row.original.status}`),
      },
    }),
    [getItemId, t],
  );

  const isRoleDependent = isProjectRoleDependent(project);

  const exportSettings = useMemo(
    () => ({
      moduleName: Module.ProjectLimits,
      queryParams: {
        project_id: projectId,
        period: formatDate(selectedMonthYear, DEFAULT_DATE_FORMAT),
      },
    }),
    [projectId, selectedMonthYear],
  );

  return (
    <QueryTable
      schema={schema}
      name={
        isRoleDependent
          ? "projectLimitsWithRoles"
          : "projectLimitsWithDepartments"
      }
      translationKey="projectLimits"
      breadcrumbs={breadcrumbs}
      query={query}
      options={options}
      tableFeatures={{
        editionHighlight: { baseline, recordKey: "stores" },
        initialColumnVisibility,
        exportSettings,
      }}
      columnOverrides={columnOverrides}
    >
      <div className="space-y-2">
        <QueryTable.Header
          breadcrumbLabel={`${name}.title.multiple`}
          breadcrumbs={breadcrumbs}
        />
        <Title>{t("projectLimits.editor.title")}</Title>
        <ProjectDetailsAndNavigation />
        <MenubarContainer>
          <QueryTable.Menubar className="mb-0 grow">
            <QueryTable.Menubar.Tools>
              <MenubarItem
                onClick={onAddLimits}
                disabled={!can_update || isProjectLocked}
              >
                {t("projectLimits.menubar.projectLimit.upsertLimits")}
                <MenubarShortcut>
                  <ModKeyChar />A
                </MenubarShortcut>
              </MenubarItem>
            </QueryTable.Menubar.Tools>
            <QueryTable.Menubar.Columns />
            <QueryTable.Menubar.Reset />
          </QueryTable.Menubar>
          <MonthYearNavigation
            value={selectedMonthYear}
            onMonthChange={onMonthChange}
            className="shrink-0 grow-0"
            popoverClassName="z-100"
            disabled={query.isFetching}
          />
          <ProjectLimitUpsertButton variant="outline" size="default" />
        </MenubarContainer>
        <ProjectLimitForm />
      </div>
      <ProjectLimitUpsertDialog selectedMonthYear={selectedMonthYear} />
    </QueryTable>
  );
};

export default memo(ProjectLimitEditor);
